import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import {
  Button,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  mergeClasses,
} from '@fluentui/react-components';
import {
  FormActions,
  useFetch,
  useFormLayoutStyles,
  useIsMobile,
} from '@moko/ui';
import {
  createTenantPricingRate,
  getTenantPricingRate,
  updateTenantPricingRate,
  type IPricingItem,
  type ITenantPricingRate,
} from '../../services/pricingService';
import type { ICatalogCurrency } from '../../services/tenantService';
import {
  dateOnlyToPickerDate,
  fromApiDateOnly,
  toApiDateOnly,
} from '../../utils/dateOnly';
import {
  emptyOverrideForm,
  getOverrideErrorMessage,
  pricingItemLabel,
  toOverrideCreateBody,
  toOverrideFormValues,
  toOverrideUpdateBody,
  validateOverrideAmount,
  validateOverrideEffectiveTo,
  type TenantPricingOverrideFormValues,
} from './tenantPricingOverrideHelpers';

export interface TenantPricingOverrideFormProps {
  tenantId: number;
  rateId?: number;
  pricingItems: IPricingItem[];
  currencies: ICatalogCurrency[];
  onClose: () => void;
  onSaved: (message: string) => void;
}

const TenantPricingOverrideForm = ({
  tenantId,
  rateId,
  pricingItems,
  currencies,
  onClose,
  onSaved,
}: TenantPricingOverrideFormProps) => {
  const styles = useFormLayoutStyles();
  const isMobile = useIsMobile();
  const isEdit = rateId !== undefined;
  const [saveError, setSaveError] = useState<string | null>(null);
  const saveAbort = useRef<AbortController | null>(null);
  const form = useForm<TenantPricingOverrideFormValues>({
    defaultValues: emptyOverrideForm,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { control, handleSubmit, reset, getValues, trigger, formState } = form;
  const { errors, isSubmitting, isSubmitted } = formState;

  useEffect(() => {
    const controller = new AbortController();
    saveAbort.current = controller;
    return () => controller.abort();
  }, []);

  const rateFetch = useFetch<ITenantPricingRate>({
    queryKey: ['tenantPricingRate', tenantId, rateId],
    queryFn: (abortController) =>
      getTenantPricingRate(abortController, rateId!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (rateFetch.data) reset(toOverrideFormValues(rateFetch.data));
  }, [rateFetch.data, reset]);

  const currencyCodes = new Set(currencies.map((currency) => currency.code));

  const onSubmit = handleSubmit(async (values) => {
    setSaveError(null);
    const abortController = saveAbort.current ?? new AbortController();
    try {
      if (isEdit) {
        const body = toOverrideUpdateBody(rateFetch.data!, values);
        if (Object.keys(body).length > 0)
          await updateTenantPricingRate(abortController, rateId, body);
        if (abortController.signal.aborted) return;
        onSaved(`Pricing override ${rateId} updated.`);
      } else {
        const result = await createTenantPricingRate(
          abortController,
          toOverrideCreateBody(values)
        );
        if (abortController.signal.aborted) return;
        onSaved(`Pricing override ${result?.id ?? ''} created.`.trim());
      }
    } catch (error) {
      if (abortController.signal.aborted) return;
      setSaveError(
        getOverrideErrorMessage(error, 'Failed to save pricing override.')
      );
    }
  });

  if (isEdit && rateFetch.loading) return <Spinner label="Loading..." />;

  if (isEdit && rateFetch.error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          {getOverrideErrorMessage(
            rateFetch.error,
            'Failed to load pricing override.'
          )}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (isEdit && !rateFetch.data) return null;

  const disabled = isSubmitting;

  return (
    <form className={styles.form} onSubmit={onSubmit} noValidate>
      {saveError && (
        <MessageBar intent="error">
          <MessageBarBody>{saveError}</MessageBarBody>
        </MessageBar>
      )}
      <Controller
        control={control}
        name="pricingItemId"
        rules={{
          validate: (value) =>
            pricingItems.some((item) => String(item.id) === value) ||
            'Pricing item is required.',
        }}
        render={({ field }) => {
          const selected = pricingItems.find(
            (item) => String(item.id) === field.value
          );
          return (
            <Field
              label="Pricing item"
              required
              validationState={errors.pricingItemId ? 'error' : 'none'}
              validationMessage={errors.pricingItemId?.message}
            >
              <Dropdown
                value={selected ? pricingItemLabel(selected) : ''}
                selectedOptions={field.value ? [field.value] : []}
                placeholder="Select pricing item"
                disabled={disabled}
                onOptionSelect={(_, data) =>
                  field.onChange(data.optionValue ?? '')
                }
              >
                {pricingItems.map((item) => (
                  <Option key={item.id} value={String(item.id)}>
                    {pricingItemLabel(item)}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          );
        }}
      />
      <div
        className={mergeClasses(
          styles.twoCol,
          isMobile && styles.twoColStacked
        )}
      >
        <div className={styles.col}>
          <Controller
            control={control}
            name="currencyCode"
            rules={{
              validate: (value) =>
                currencyCodes.has(value) || 'Select a supported currency.',
            }}
            render={({ field }) => (
              <Field
                label="Currency"
                required
                validationState={errors.currencyCode ? 'error' : 'none'}
                validationMessage={errors.currencyCode?.message}
              >
                <Dropdown
                  value={field.value}
                  selectedOptions={field.value ? [field.value] : []}
                  placeholder="Select currency"
                  disabled={disabled}
                  onOptionSelect={(_, data) =>
                    field.onChange(data.optionValue ?? '')
                  }
                >
                  {currencies.map((currency) => (
                    <Option
                      key={currency.code}
                      value={currency.code}
                      text={currency.code}
                    >
                      {`${currency.code} - ${currency.name}`}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            )}
          />
        </div>
        <div className={styles.col}>
          <Controller
            control={control}
            name="amount"
            rules={{ validate: validateOverrideAmount }}
            render={({ field }) => (
              <Field
                label="Amount"
                required
                validationState={errors.amount ? 'error' : 'none'}
                validationMessage={errors.amount?.message}
              >
                <Input
                  inputMode="decimal"
                  value={field.value}
                  disabled={disabled}
                  onChange={(_, data) => field.onChange(data.value)}
                  onBlur={field.onBlur}
                />
              </Field>
            )}
          />
        </div>
      </div>
      <div
        className={mergeClasses(
          styles.twoCol,
          isMobile && styles.twoColStacked
        )}
      >
        <div className={styles.col}>
          <Controller
            control={control}
            name="effectiveFrom"
            rules={{
              validate: (value) =>
                !!fromApiDateOnly(value) || 'Effective from is required.',
            }}
            render={({ field }) => (
              <Field
                label="Effective from"
                required
                validationState={errors.effectiveFrom ? 'error' : 'none'}
                validationMessage={errors.effectiveFrom?.message}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  value={dateOnlyToPickerDate(field.value)}
                  disabled={disabled}
                  onSelectDate={(selected) => {
                    field.onChange(toApiDateOnly(selected));
                    if (isSubmitted) void trigger('effectiveTo');
                  }}
                />
              </Field>
            )}
          />
        </div>
        <div className={styles.col}>
          <Controller
            control={control}
            name="effectiveTo"
            rules={{
              validate: (value) =>
                validateOverrideEffectiveTo(value, getValues('effectiveFrom')),
            }}
            render={({ field }) => (
              <Field
                label="Effective to"
                hint="Leave empty for no end date."
                validationState={errors.effectiveTo ? 'error' : 'none'}
                validationMessage={errors.effectiveTo?.message}
              >
                <div style={{ display: 'flex', gap: 4 }}>
                  <DatePicker
                    style={{ flex: 1, minWidth: 0 }}
                    value={dateOnlyToPickerDate(field.value)}
                    disabled={disabled}
                    onSelectDate={(selected) =>
                      field.onChange(toApiDateOnly(selected))
                    }
                  />
                  <Button
                    appearance="subtle"
                    disabled={disabled || !field.value}
                    onClick={() => field.onChange('')}
                  >
                    Clear
                  </Button>
                </div>
              </Field>
            )}
          />
        </div>
      </div>
      <FormActions
        disabled={disabled}
        submitting={isSubmitting}
        submitLabel={isEdit ? 'Save' : 'Create'}
        onClose={onClose}
      />
    </form>
  );
};

export default TenantPricingOverrideForm;
